export function TaxPlanningStrategies() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Advanced Tax Planning Strategies
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Maximize tax benefits and minimize liability through strategic seller financing structures
          </p>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-md mb-12 max-w-4xl mx-auto">
          <div className="flex items-start">
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-3xl text-amber-600"></i>
            </div>
            <div className="ml-4">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Critical Tax Disclaimer</h4>
              <p className="text-gray-700 leading-relaxed">
                <strong>Every tax situation is unique and different.</strong> The strategies discussed below are general concepts and may not apply to your specific circumstances. Tax laws are complex, vary by state, and change frequently. You must consult with a qualified CPA, tax attorney, or enrolled agent who understands your complete financial picture before implementing any tax strategy. This information is for educational purposes only and is not tax advice.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="bg-gray-50 rounded-lg p-8 shadow-md">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                <i className="ri-calendar-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Installment Sale Method</h3>
            </div>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Under IRS Section 453, installment sales allow you to recognize capital gains proportionally as you receive payments rather than all at once in the year of sale.
            </p>
            <div className="bg-white rounded-lg p-6 mb-4">
              <h4 className="text-lg font-bold text-gray-900 mb-3">How It Works:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Calculate gross profit ratio (gain ÷ contract price)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Apply ratio to each principal payment received</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Report only that portion as taxable gain each year</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Interest income is taxed as ordinary income</span>
                </li>
              </ul>
            </div>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-4 rounded-md">
              <p className="text-gray-700 text-sm">
                <strong>Example:</strong> If your gross profit ratio is 60%, then 60% of each principal payment is taxable gain, while 40% is return of basis (not taxed).
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 shadow-md">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                <i className="ri-line-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Tax Bracket Management</h3>
            </div>
            <p className="text-gray-700 mb-4 leading-relaxed">
              By spreading income over multiple years, you can potentially stay in lower tax brackets and avoid the higher rates that would apply to a lump-sum gain.
            </p>
            <div className="bg-white rounded-lg p-6 mb-4">
              <h4 className="text-lg font-bold text-gray-900 mb-3">Strategic Benefits:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Avoid jumping into higher capital gains tax brackets</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Reduce or avoid Net Investment Income Tax (3.8%)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Maintain eligibility for certain deductions and credits</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span>Coordinate with other income sources for optimal planning</span>
                </li>
              </ul>
            </div>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-4 rounded-md">
              <p className="text-gray-700 text-sm">
                <strong>Note:</strong> High-income earners may face 20% capital gains rates plus 3.8% NIIT. Spreading income can help manage these thresholds.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Additional Tax Optimization Strategies
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-exchange-line text-xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Depreciation Recapture Planning</h4>
                </div>
                <p className="text-gray-700 mb-3 leading-relaxed">
                  Depreciation recapture (taxed at 25%) can be spread over the installment period, reducing the immediate tax impact.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Recapture is allocated proportionally with gain</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Reduces year-one tax burden significantly</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Coordinate with cost segregation studies</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-file-list-3-line text-xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Interest Income Deductions</h4>
                </div>
                <p className="text-gray-700 mb-3 leading-relaxed">
                  If you have an existing mortgage on the property, interest you pay may offset interest income you receive.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Investment interest expense deduction</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Reduces net taxable interest income</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Subject to investment interest limitations</span>
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-gift-line text-xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Estate Planning Benefits</h4>
                </div>
                <p className="text-gray-700 mb-3 leading-relaxed">
                  Installment notes can be valuable estate planning tools for transferring wealth to heirs with potential tax advantages.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Notes can be gifted or inherited</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Potential valuation discounts available</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Income stream continues to beneficiaries</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-calculator-line text-xl text-white"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Charitable Planning Options</h4>
                </div>
                <p className="text-gray-700 mb-3 leading-relaxed">
                  Seller financing notes can be used in charitable giving strategies for additional tax benefits.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Donate notes to charitable remainder trusts</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Receive income tax deductions</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                    <span>Reduce estate tax exposure</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/66dc7d2b944d4905ac9cddb9f70d4617.jpg"
              alt="Tax planning strategies consultation for seller financing"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Working with Tax Professionals
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The complexity of seller financing tax planning requires <strong>experienced tax professionals</strong> who understand real estate transactions, installment sales, and advanced tax strategies. Don't attempt to navigate these waters alone.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Your tax advisor should analyze your complete financial picture, including your current tax bracket, other income sources, state tax implications, estate planning goals, and long-term financial objectives. They can model different scenarios to determine the optimal structure for your situation.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-md">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Essential Team Members</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-user-star-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>CPA or Tax Attorney:</strong> Structure transaction for optimal tax treatment</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-user-star-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Real Estate Attorney:</strong> Prepare legal documents and ensure compliance</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-user-star-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Financial Advisor:</strong> Integrate strategy with overall financial plan</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-user-star-line text-xl text-teal-600 mr-2 flex-shrink-0"></i>
                  <span><strong>Estate Planner:</strong> Coordinate with long-term wealth transfer goals</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-amber-600 to-amber-700 rounded-lg p-8 md:p-12 text-white">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-alert-line text-5xl text-white"></i>
            </div>
            <h3 className="text-3xl font-bold mb-4">Remember: Professional Guidance Is Essential</h3>
            <p className="text-xl text-amber-100 max-w-4xl mx-auto leading-relaxed">
              Tax laws are complex and constantly changing. What works for one seller may not work for another. Always consult with qualified tax professionals before implementing any seller financing strategy. The cost of professional advice is minimal compared to the potential tax savings and risk mitigation it provides.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}